const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const UK_DATE_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const UK_DASH_DATE_REGEX = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;

const DAY_MS = 24 * 60 * 60 * 1000;

const pad = (value) => String(value).padStart(2, "0");

const createLocalDate = (year, month, day) => {
  return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
};

const normaliseDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  return createLocalDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );
};

const toNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === "") return fallback;

  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
};

export const parseDateValue = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return normaliseDateOnly(value.toDate());
  }

  if (value instanceof Date) {
    return normaliseDateOnly(value);
  }

  if (typeof value === "number") {
    return normaliseDateOnly(new Date(value));
  }

  const stringValue = String(value).trim();

  if (!stringValue) return null;

  if (ISO_DATE_REGEX.test(stringValue)) {
    const [year, month, day] = stringValue.split("-");
    return createLocalDate(year, month, day);
  }

  const ukSlashMatch = stringValue.match(UK_DATE_REGEX);

  if (ukSlashMatch) {
    const [, day, month, year] = ukSlashMatch;
    return createLocalDate(year, month, day);
  }

  const ukDashMatch = stringValue.match(UK_DASH_DATE_REGEX);

  if (ukDashMatch) {
    const [, day, month, year] = ukDashMatch;
    return createLocalDate(year, month, day);
  }

  const fallbackDate = new Date(stringValue);

  return normaliseDateOnly(fallbackDate);
};

export const parseHolidayDate = parseDateValue;

export const toDateKey = (value) => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) return "";

  const year = parsedDate.getFullYear();
  const month = pad(parsedDate.getMonth() + 1);
  const day = pad(parsedDate.getDate());

  return `${year}-${month}-${day}`;
};

export const formatDisplayDate = (value) => {
  const dateKey = toDateKey(value);

  if (!dateKey) return "";

  const [year, month, day] = dateKey.split("-");

  return `${day}/${month}/${year}`;
};

export const formatDisplayDateRange = (startDate, endDate) => {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);

  if (!start && !end) return "";
  if (start && !end) return start;
  if (!start && end) return end;

  return `${start} to ${end}`;
};

export const getDatesBetween = (startDate, endDate) => {
  const start = parseDateValue(startDate);
  const end = parseDateValue(endDate);

  if (!start || !end) return [];

  const dates = [];
  const current = new Date(start);

  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

export const isWeekend = (value) => {
  const date = parseDateValue(value);

  if (!date) return false;

  const day = date.getDay();

  return day === 0 || day === 6;
};

export const isValidDateValue = (value) => {
  return Boolean(toDateKey(value));
};

const toUtcDateOnlyMs = (date) => {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getInclusiveDays = (startDate, endDate) => {
  const start = parseDateValue(startDate);
  const end = parseDateValue(endDate);

  if (!start || !end) return 0;
  if (start > end) return 0;

  const startMs = toUtcDateOnlyMs(start);
  const endMs = toUtcDateOnlyMs(end);

  return Math.floor((endMs - startMs) / DAY_MS) + 1;
};

// export const roundHolidayEntitlement = (value, rounding = "upHalf") => {
//   const number = Number(value);

//   if (!Number.isFinite(number)) return 0;

//   switch (rounding) {
//     case "none":
//       return Number(number.toFixed(2));

//     case "nearestHalf":
//       return Math.round(number * 2) / 2;

//     case "downHalf":
//       return Math.floor(number * 2) / 2;

//     case "upHalf":
//     default:
//       return Math.ceil(number * 2) / 2;
//   }
// };
const roundHolidayEntitlement = (value, rounding = "up") => {
  const number = Number(value);

  if (!Number.isFinite(number)) return 0;

  switch (rounding) {
    case "none":
      return Number(number.toFixed(2));

    case "nearestHalf":
      return Math.round(number * 2) / 2;

    case "downHalf":
      return Math.floor(number * 2) / 2;

    case "upHalf":
      return Math.ceil(number * 2) / 2;

    case "up":
    default:
      return Math.ceil(number);
  }
};

export const getHolidayYearRange = ({
  holidayYear,
  holidayYearStartMonth = 1,
  holidayYearStartDay = 1,
}) => {
  const year = Number(holidayYear) || new Date().getFullYear();

  const yearStart = createLocalDate(
    year,
    holidayYearStartMonth,
    holidayYearStartDay,
  );

  const yearEnd = createLocalDate(
    year + 1,
    holidayYearStartMonth,
    holidayYearStartDay - 1,
  );

  return {
    year,
    yearStart,
    yearEnd,
  };
};

export const calculateHolidayEntitlementBreakdown = ({
  employmentStartDate,
  employmentEndDate,

  // aliases, in case other parts still use these names
  startDate,
  joinDate,
  dateJoined,
  leavingDate,
  endDate,

  annualEntitlement = 20,
  holidayYear,
  holidayYearStartMonth = 1,
  holidayYearStartDay = 1,
  rounding = "upHalf",
}) => {
  const fullAnnualEntitlement = toNumber(annualEntitlement, 20);

  const { year, yearStart, yearEnd } = getHolidayYearRange({
    holidayYear,
    holidayYearStartMonth,
    holidayYearStartDay,
  });

  const parsedEmploymentStartDate = parseHolidayDate(
    employmentStartDate || startDate || joinDate || dateJoined,
  );

  const parsedEmploymentEndDate = parseHolidayDate(
    employmentEndDate || leavingDate || endDate,
  );

  const effectiveStartDate =
    parsedEmploymentStartDate && parsedEmploymentStartDate > yearStart
      ? parsedEmploymentStartDate
      : yearStart;

  const effectiveEndDate =
    parsedEmploymentEndDate && parsedEmploymentEndDate < yearEnd
      ? parsedEmploymentEndDate
      : yearEnd;

  const totalDaysInHolidayYear = getInclusiveDays(yearStart, yearEnd);

  const isOutsideHolidayYear =
    effectiveStartDate > yearEnd || effectiveEndDate < yearStart;

  const employedDaysInHolidayYear = isOutsideHolidayYear
    ? 0
    : getInclusiveDays(effectiveStartDate, effectiveEndDate);

  const rawEntitlement =
    totalDaysInHolidayYear > 0
      ? (fullAnnualEntitlement * employedDaysInHolidayYear) /
        totalDaysInHolidayYear
      : 0;

  const entitlement = roundHolidayEntitlement(rawEntitlement, rounding);

  return {
    year,
    annualEntitlement: fullAnnualEntitlement,
    entitlement,
    rawEntitlement,
    totalDaysInHolidayYear,
    employedDaysInHolidayYear,
    employmentStartDate: parsedEmploymentStartDate,
    employmentEndDate: parsedEmploymentEndDate,
    effectiveStartDate: isOutsideHolidayYear ? null : effectiveStartDate,
    effectiveEndDate: isOutsideHolidayYear ? null : effectiveEndDate,
    yearStart,
    yearEnd,
  };
};

export const calculateHolidayEntitlementForYear = (options = {}) => {
  return calculateHolidayEntitlementBreakdown(options).entitlement;
};
